/**
 * KAN-656: кадрирование фото профиля перед отправкой в Telegram. Квадратное
 * окно с круглой маской, перетаскивание указателем (мышь, палец) или
 * стрелками, ползунок масштаба 1..3. Состояние кадра (offset, zoom) живёт
 * здесь; наружу уходит картинка и квадрат на ней через onChange.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  clampOffset,
  cropRect,
  fitScale,
  MAX_ZOOM,
  MIN_ZOOM,
  type CropSelection,
  type Offset,
} from '@/lib/avatarCrop';

export const CROP_HINT =
  'Перетащите фото и подберите масштаб: в круг попадёт то, что уйдёт в Telegram';

const DEFAULT_SIZE = 224;
const KEY_STEP = 8;
const CENTER: Offset = { x: 0, y: 0 };

/** Захват указателя не обязателен: без него (jsdom, старые браузеры) тянем как есть. */
function capture(el: HTMLElement, id: number, on: boolean): void {
  try {
    if (on) el.setPointerCapture?.(id);
    else el.releasePointerCapture?.(id);
  } catch {
    // Указатель уже неактивен: ничего не делаем.
  }
}

interface Props {
  file: File;
  onChange: (selection: CropSelection) => void;
  /** Сторона окна в px; на узких экранах окно ужимается по ширине контейнера. */
  size?: number;
}

interface Dims {
  w: number;
  h: number;
}

/** Сторона окна: не шире контейнера (0 в jsdom и до первого замера). */
function useViewportSize(
  ref: React.RefObject<HTMLDivElement>,
  wanted: number
): number {
  const [measured, setMeasured] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = (): void => setMeasured(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return measured > 0 ? Math.min(wanted, measured) : wanted;
}

export function AvatarCropper({
  file,
  onChange,
  size = DEFAULT_SIZE,
}: Props): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const S = useViewportSize(boxRef, size);
  const [dims, setDims] = useState<Dims | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [rawOffset, setRawOffset] = useState<Offset>(CENTER);
  const drag = useRef<{ id: number; start: Offset; from: Offset } | null>(null);

  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  // Новый файл: кадр по центру без увеличения.
  useEffect(() => {
    setDims(null);
    setZoom(MIN_ZOOM);
    setRawOffset(CENTER);
  }, [file]);

  const w = dims?.w ?? 0;
  const h = dims?.h ?? 0;
  // Мемо: стабильный объект, иначе onChange уходил бы на каждый рендер.
  const offset = useMemo(
    () => (dims ? clampOffset(rawOffset, w, h, S, zoom) : CENTER),
    [dims, rawOffset, w, h, S, zoom]
  );
  const rect = useMemo(
    () => (dims ? cropRect(w, h, S, zoom, offset) : null),
    [dims, w, h, S, zoom, offset]
  );

  useEffect(() => {
    const image = imgRef.current;
    if (!rect || !image) return;
    onChange({ image, rect });
  }, [onChange, rect]);

  const moveTo = (next: Offset): void => {
    if (dims) setRawOffset(clampOffset(next, w, h, S, zoom));
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dims) return;
    drag.current = {
      id: e.pointerId,
      start: { x: e.clientX, y: e.clientY },
      from: offset,
    };
    capture(e.currentTarget, e.pointerId, true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    moveTo({
      x: d.from.x + (e.clientX - d.start.x),
      y: d.from.y + (e.clientY - d.start.y),
    });
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    capture(e.currentTarget, e.pointerId, false);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const step: Record<string, Offset> = {
      ArrowLeft: { x: KEY_STEP, y: 0 },
      ArrowRight: { x: -KEY_STEP, y: 0 },
      ArrowUp: { x: 0, y: KEY_STEP },
      ArrowDown: { x: 0, y: -KEY_STEP },
    };
    const delta = step[e.key];
    if (!delta) return;
    e.preventDefault();
    moveTo({ x: offset.x + delta.x, y: offset.y + delta.y });
  };

  const k = dims ? fitScale(w, h, S) * zoom : 1;
  const imgStyle: React.CSSProperties = dims
    ? {
        width: w * k,
        height: h * k,
        transform: `translate(${(S - w * k) / 2 + offset.x}px, ${
          (S - h * k) / 2 + offset.y
        }px)`,
      }
    : { visibility: 'hidden' };

  return (
    <div className="flex flex-col items-start gap-2" data-testid="avatar-cropper">
      <div
        ref={boxRef}
        role="img"
        aria-label="Область фото профиля"
        tabIndex={0}
        data-testid="avatar-crop-area"
        className="relative max-w-full cursor-grab touch-none select-none overflow-hidden rounded-md bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-gray-400 active:cursor-grabbing"
        style={{ width: S, height: S }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        <img
          ref={imgRef}
          src={url}
          alt=""
          draggable={false}
          data-testid="avatar-crop-image"
          className="absolute left-0 top-0 max-w-none origin-top-left"
          style={imgStyle}
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            if (naturalWidth > 0 && naturalHeight > 0) {
              setDims({ w: naturalWidth, h: naturalHeight });
            }
          }}
        />
        {/* Круглая маска: всё вне круга затемнено. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)' }}
        />
      </div>
      <div className="flex w-full items-center gap-2" style={{ maxWidth: S }}>
        <span className="text-xs text-gray-600">Масштаб</span>
        <Slider
          aria-label="Масштаб"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={[zoom]}
          disabled={!dims}
          onValueChange={([z]) => setZoom(z)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!dims}
          onClick={() => {
            setZoom(MIN_ZOOM);
            setRawOffset(CENTER);
          }}
        >
          По центру
        </Button>
      </div>
      <p className="text-xs text-gray-500">{CROP_HINT}</p>
    </div>
  );
}
